import React, { useState } from 'react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, updateDoc } from 'firebase/firestore';
import { storage, db } from '../../config/firebase.js';
import { useAuth } from '../../hooks/useAuth';
import Button from './Button';

const AvatarUpload = ({ 
  userId, 
  currentAvatarUrl, 
  displayName, 
  onAvatarUpdate,
  size = 'medium',
  showRemoveButton = false  // Add this prop - default to false
}) => {
  const { userProfile, updateUserProfile } = useAuth();
  const [uploading, setUploading] = useState(false);

  const sizeClasses = {
    small: 'w-10 h-10',
    medium: 'w-16 h-16',
    large: 'w-24 h-24'
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be smaller than 5MB');
      return;
    }

    try {
      setUploading(true);

      // Create storage reference - ensure no special characters in filename
      const fileName = file.name.replace(/[^a-zA-Z0-9.]/g, '_');
      const storageRef = ref(storage, `avatars/${userId}/${Date.now()}_${fileName}`);
      
      console.log('Uploading to:', storageRef.fullPath); // Debug log
      
      // Upload file
      const snapshot = await uploadBytes(storageRef, file);
      
      // Get download URL
      const downloadURL = await getDownloadURL(snapshot.ref);

      // Update user profile in Firestore
      await updateDoc(doc(db, 'users', userId), {
        avatarUrl: downloadURL,
        updatedAt: new Date().toISOString()
      });

      // If it's the current user, update auth context
      if (userId === userProfile?.uid) {
        await updateUserProfile({ avatarUrl: downloadURL });
      }

      // Call callback
      onAvatarUpdate?.(downloadURL);

      console.log('Avatar uploaded successfully:', downloadURL);

    } catch (error) {
      console.error('Error uploading avatar:', error);
      alert('Failed to upload avatar: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveAvatar = async () => {
    if (!window.confirm('Are you sure you want to remove your avatar?')) {
      return;
    }

    try {
      setUploading(true);
      
      // Update user profile in Firestore
      await updateDoc(doc(db, 'users', userId), {
        avatarUrl: '',
        updatedAt: new Date().toISOString()
      });

      // If it's the current user, update auth context
      if (userId === userProfile?.uid) {
        await updateUserProfile({ avatarUrl: '' });
      }

      // Call callback
      onAvatarUpdate?.('');

    } catch (error) {
      console.error('Error removing avatar:', error);
      alert('Failed to remove avatar');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex flex-col items-center space-y-2">
      <div className="relative">
        {currentAvatarUrl ? (
          <img
            src={currentAvatarUrl}
            alt={displayName}
            className={`${sizeClasses[size]} rounded-full object-cover border-2 border-gray-200 dark:border-gray-700`}
          />
        ) : (
          <div className={`${sizeClasses[size]} rounded-full bg-blue-500 flex items-center justify-center border-2 border-gray-200 dark:border-gray-700`}>
            <span className="text-white font-semibold">
              {displayName?.charAt(0).toUpperCase() || '?'}
            </span>
          </div>
        )}
        
        {userProfile?.uid === userId && (
          <label className="absolute -bottom-2 -right-2 bg-white dark:bg-gray-800 rounded-full p-1 shadow-lg border border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            <input
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              className="hidden"
              disabled={uploading}
            />
            <svg className="w-4 h-4 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </label>
        )}
      </div>

      {/* Only show remove button if explicitly enabled AND user owns the avatar AND avatar exists */}
      {showRemoveButton && userProfile?.uid === userId && currentAvatarUrl && (
        <Button
          variant="outline"
          size="small"
          loading={uploading}
          onClick={handleRemoveAvatar}
        >
          Remove
        </Button>
      )}
    </div>
  );
};

export default AvatarUpload;